import { OptionType } from '@/components/AutoComplete';
import { formatterDate } from '@/components/Table/utils';
import { isDefAndNotNull } from '@/utils/def';
import { getToday } from '@/utils/formatTime';
import moment from 'moment';
import { createSelector } from 'reselect';

import { PricingSummaryFormValues } from './PricingSummary/types';
import {
  DEBT_TYPE_FLOAT,
  defaultValuesPricingSummary,
  frequencyMonths,
} from './constants';
import { CapMode, UserWidgetType, ValidationType, WidgetKeys } from './enums';
import { selectIrAnalysisState } from './store/selectors';
import {
  CashflowThroughTimeState,
  DebtHedgeScheduleState,
  DebtSchedulePeriod,
  GraphGroup,
  IRMarketEnvironmentState,
  PricingSummaryState,
  UserWidgetResponse,
} from './store/types';
import {
  DownloadCashflowsThroughTimePayload,
  DownloadDebtHedgeSchedulePayload,
  DownloadIRMarketEnvironmentPayload,
  DownloadPricingSummaryPayload,
  GraphGroupPayload,
  UserWidgetRow,
} from './types';

export const getDefaultEffectiveDate = (runDate?: string): string => {
  return runDate || moment().format('YYYY-MM-DD');
};

export const getDefaultMaturityDate = (
  runDate?: string,
  years: number = 3,
): string => {
  const baseDate = runDate ? moment(runDate) : moment();
  return baseDate.add(years, 'years').format('YYYY-MM-DD');
};

export const getTitleFormInput = (property: any) => {
  if ('title' in property) return property.title;
  return '';
};

export const getCapModeName = (value?: boolean) =>
  value ? CapMode.RELATIVE_MODE : CapMode.ABSOLUTE_MODE;

export const mapIRMarketToPayload = (
  irMarket: IRMarketEnvironmentState,
): DownloadIRMarketEnvironmentPayload => ({
  index_id: irMarket.irIndex ?? 0,
  run_dates: irMarket.runDates.map(formatterDate),
  security_ids: irMarket.swapTenors.map((id) => Number(id)),
  start_date: formatterDate(irMarket.startDate),
  end_date: formatterDate(irMarket.endDate),
  years: irMarket.years,
});
export const mapIRMarketToPayload2 = (
  irMarket: IRMarketEnvironmentState,
): DownloadIRMarketEnvironmentPayload => ({
  index_id: irMarket.irIndex ?? 0,
  run_dates: irMarket.runDates,
  security_ids: irMarket.swapTenors,
  start_date: irMarket.startDate,
  end_date: irMarket.endDate,
  years: irMarket.years,
});

export const mapDebtHedgeToPayload = (
  debtHedge: DebtHedgeScheduleState,
): DownloadDebtHedgeSchedulePayload => {
  return {
    debt_id_list: debtHedge.debts
      ? debtHedge.debts.split(',').map((id) => Number(id.trim()))
      : null,
    transaction_id_list: debtHedge.trades
      ? debtHedge.trades.split(',').map((id) => Number(id.trim()))
      : null,
    company_entity_id:
      debtHedge.debts || debtHedge.trades
        ? null
        : isDefAndNotNull(debtHedge.companyEntity)
          ? typeof debtHedge.companyEntity === 'string'
            ? debtHedge.companyEntity
            : String((debtHedge.companyEntity as OptionType).value)
          : debtHedge.validations.debts.debtCompanyEntityId ||
            debtHedge.validations.trades.tradeCompanyEntityId ||
            null,
    run_date: formatterDate(debtHedge.runDate),
    hedge_percentage: debtHedge.hedgePercentage
      ? debtHedge.hedgePercentage.split(',').map((v) => Number(v))
      : [],
    new_hedge_start_date: formatterDate(debtHedge.hedgeStartDate),
    new_hedge_end_date: formatterDate(debtHedge.hedgeEndDate),
    debt_start_date: debtHedge.debtStartDate
      ? formatterDate(debtHedge.debtStartDate)
      : null,
    debt_end_date: debtHedge.debtEndDate
      ? formatterDate(debtHedge.debtEndDate)
      : null,
    frequency: debtHedge.frequency || null,
    amort_rate: debtHedge.amortRate ? Number(debtHedge.amortRate) : null,
    notional_value: debtHedge.notionalAmount
      ? Number(debtHedge.notionalAmount)
      : null,
    notional_currency_id: debtHedge.notionalCurrency
      ? Number(debtHedge.notionalCurrency)
      : null,
    new_debt_comp_ent_id:
      debtHedge.validations.debts.debtCompanyEntityId ||
      debtHedge.validations.trades.tradeCompanyEntityId,
    amortizing_notional: debtHedge.hedgePercentage
      ? debtHedge.hedgePercentage.split(',').map((v) => Number(v))[0]
      : null,
  };
};

const mapGraphGroupToPayload = (group: GraphGroup): GraphGroupPayload => ({
  effective_date: formatterDate(group.effectiveDate),
  maturity_date: formatterDate(group.maturityDate),
  swap_charge: Number(group.swapCharge),
  graph: group.graph,
  breakage: group.breakage.map((id) => Number(id)),
});

export const mapPricingSummaryToPayload = (
  pricing: PricingSummaryState,
): DownloadPricingSummaryPayload => {
  const shouldIncludeHedgePercentage =
    pricing.debt_and_hedge_enabled ||
    pricing.enable_hedge_breakage ||
    pricing.cftt;

  const payload: any = {
    currency_id:
      isDefAndNotNull(pricing?.currency) && 'value' in pricing.currency
        ? Number(pricing.currency.value)
        : 0,
    ir_index_id:
      isDefAndNotNull(pricing?.irIndex) && 'value' in pricing.irIndex
        ? Number(pricing.irIndex.value)
        : 0,
    market_date: formatterDate(pricing.runDate),
    frequency: pricing.frequency?.value,
    graph_data: Object.values(pricing.graphGroups).map(mapGraphGroupToPayload),
    funding_spread: pricing.fundingSpread,
    normal_vol_adjustment: pricing.normalVolAdjustment,
    notional: pricing.notional,
    swap: pricing.swap,
    swap_floor: pricing.swapFloor,
    floor_strike: pricing.floorStrike,
    cap: pricing.cap,
    cap_strike_relative: pricing.capMode,
    cap_strikes_list: !pricing.capMode
      ? pricing.capStrikesList.split(',').map((v) => Number(v.trim()))
      : [],
    cap_strikes_atm_bps_list: pricing.capMode
      ? pricing.capStrikesAtmBpsList.split(',').map((v) => Number(v.trim()))
      : [],
    zero_cost_collar: pricing.zeroCostCollar,
    day_count: pricing.cftt ? pricing.dayCount : 0,
    cftt_enabled: pricing.cftt,
    enable_hedge_breakage: pricing.enable_hedge_breakage,
    debt_and_hedge_enabled: pricing.debt_and_hedge_enabled,
  };

  // Only include hedge_percentages and amort_rate if at least one flag is enabled
  if (shouldIncludeHedgePercentage) {
    if (pricing.hedgePercentage) {
      payload.hedge_percentages = pricing.hedgePercentage
        .split(',')
        .map((v) => Number(v.trim()));
    }
    if (pricing.amortRate !== undefined && pricing.amortRate !== null) {
      payload.amort_rate = pricing.amortRate;
    }
  }

  // Virtual debt fields — only when the Debt & Hedge Schedule flag is enabled.
  if (pricing.debt_and_hedge_enabled) {
    const debtType = pricing.debtType ?? DEBT_TYPE_FLOAT;
    payload.debt_type = debtType;
    payload.debt_day_count = pricing.debtDayCount;

    if (debtType === DEBT_TYPE_FLOAT) {
      payload.debt_spread = pricing.debtSpread;
      payload.debt_floor = pricing.debtFloor;
    } else {
      payload.debt_coupon = pricing.debtCoupon;
    }

    // A user-customized schedule overrides notional + amort_rate: send the
    // explicit periods and blank out amort_rate.
    if (pricing.debtScheduleOverridden && pricing.debtSchedule?.length) {
      payload.debt_schedule = pricing.debtSchedule.map((period) => ({
        accrual_start: formatterDate(period.accrual_start),
        accrual_end: formatterDate(period.accrual_end),
        notional: Number(period.notional),
        spread: Number(period.spread),
      }));
      payload.amort_rate = '';
    }
  }

  return payload;
};

/**
 * Builds accrual periods from a date range at the given frequency and applies
 * the amort rate cumulatively (declining balance): period[i] = period[i-1] ×
 * amortRate, seeded from the base notional. When no amort rate is supplied,
 * every period keeps the base notional.
 */
export const generateDebtSchedule = ({
  startDate,
  endDate,
  frequency,
  baseNotional,
  amortRate,
  spread,
}: {
  startDate: string;
  endDate: string;
  frequency: string;
  baseNotional: number;
  amortRate?: number;
  spread?: number;
}): DebtSchedulePeriod[] => {
  const months = frequencyMonths[frequency] ?? 3;
  if (!startDate || !endDate) return [];

  const start = moment(startDate);
  const end = moment(endDate);
  if (!start.isValid() || !end.isValid() || !start.isBefore(end)) return [];

  const periods: DebtSchedulePeriod[] = [];
  let cursor = start.clone();
  let notional = baseNotional;
  const hasAmort = isDefAndNotNull(amortRate) && Number(amortRate) > 0;

  while (cursor.isBefore(end)) {
    const accrualStart = cursor.clone();
    const accrualEnd = moment.min(cursor.clone().add(months, 'months'), end);

    notional = hasAmort ? notional * Number(amortRate) : baseNotional;

    periods.push({
      accrual_start: accrualStart.format('YYYY-MM-DD'),
      accrual_end: accrualEnd.format('YYYY-MM-DD'),
      notional: Math.round(notional),
      spread: Number(spread ?? 0),
    });

    cursor = accrualEnd;
  }

  return periods;
};

export const mapCashflowThroughTimeToPayload = (
  cashflow: CashflowThroughTimeState,
): DownloadCashflowsThroughTimePayload => ({
  transaction_id_list: cashflow.trades
    ? cashflow.trades.split(',').map((id) => Number(id.trim()))
    : [],
  debt_id_list: cashflow.debts
    ? cashflow.debts.split(',').map((id) => Number(id.trim()))
    : [],
  comp_ent_id:
    cashflow.companyEntity?.value !== undefined
      ? String(cashflow.companyEntity.value)
      : cashflow.validations.trades.tradeCompanyEntityId !== undefined
        ? String(cashflow.validations.trades.tradeCompanyEntityId)
        : cashflow.validations.debts.debtCompanyEntityId !== undefined
          ? String(cashflow.validations.debts.debtCompanyEntityId)
          : null,
  run_date: formatterDate(cashflow.runDate),
  daycount: cashflow.dayCount,
});

export const validateCompanyIds = (
  widgetKey: WidgetKeys,
  widgetData: DebtHedgeScheduleState | CashflowThroughTimeState,
  enabledWidget: boolean,
  validationErrors: string[],
) => {
  if (!enabledWidget) return;

  const { trades, debts } = widgetData.validations;
  const tradeId = trades.tradeCompanyEntityId;
  const debtId = debts.debtCompanyEntityId;

  const widgetTitleMap: Partial<Record<WidgetKeys, string>> = {
    [WidgetKeys.DEBT_HEDGE_SCHEDULE]: 'Debt & Hedge Schedule',
    [WidgetKeys.CASHFLOW_THROUGH_TIME]: 'Cashflow Through Time',
  };

  if (tradeId && debtId && tradeId !== debtId) {
    validationErrors.push(
      `For ${widgetTitleMap[widgetKey]}, trades and debts must belong to the same company.`,
    );
  }
};

export const generateValidationKey = (widget: string, date: string) => {
  return `${widget}_${date}`;
};

export const createSelectValidationByWidget = (
  validationType: ValidationType,
  widgetKey: WidgetKeys,
) =>
  createSelector(selectIrAnalysisState, (state) => {
    const widgetState =
      widgetKey === WidgetKeys.CASHFLOW_THROUGH_TIME
        ? state.cashflowThroughTime
        : widgetKey === WidgetKeys.DEBT_HEDGE_SCHEDULE
          ? state.debtHedgeSchedule
          : undefined;

    return (
      widgetState?.validations?.[validationType] ?? {
        loading: false,
        valid: null,
        error: null,
      }
    );
  });

export const buildGraphGroups = (
  values: Record<string, any>,
  extraGroupsCount: number,
) => {
  const graphGroups: Record<number, GraphGroup> = {};
  for (let i = 0; i < extraGroupsCount; i++) {
    const idx = i + 1;
    graphGroups[idx] = {
      effectiveDate: String(values[`effectiveDate${idx}`] ?? getToday()),
      maturityDate: String(values[`maturityDate${idx}`] ?? getToday()),
      swapCharge: String(values[`swapCharge${idx}`] ?? ''),
      graph: !!values[`graph${idx}`],
      breakage: Array.isArray(values[`breakage${idx}`])
        ? (values[`breakage${idx}`] as OptionType[]).map((opt) =>
            Number(opt.value),
          )
        : [],
    };
  }
  return graphGroups;
};

export const getUserWidgetTypeLabel = (type: string): string => {
  switch (type) {
    case UserWidgetType.IRA:
      return 'Interest Rate Analysis';
    case UserWidgetType.DEAL_CONTINGENT:
      return 'Deal Contingent';
    default:
      return type;
  }
};

export const mapUserWidgetResponseToRow = (
  response: UserWidgetResponse,
): UserWidgetRow => ({
  id: response.widget_id,
  title: response.text_input,
  type: getUserWidgetTypeLabel(response.widget_type),
  companyEntity: response.company_entity_name || '',
  createdAt: response.created_at,
});

export const mapInitialValuesToForm = (
  initialValues: DownloadPricingSummaryPayload,
): Partial<PricingSummaryFormValues> => {
  const formValues: Partial<PricingSummaryFormValues> = {};

  if (initialValues.currency_id !== undefined) {
    formValues.currency = {
      value: String(initialValues.currency_id),
      label: '',
    };
  }

  if (initialValues.ir_index_id !== undefined) {
    formValues.irIndex = {
      value: initialValues.ir_index_id,
      label: '',
    };
  }

  formValues.cap = initialValues.cap ?? false;
  formValues.swap = initialValues.swap ?? false;
  formValues.swapFloor = initialValues.swap_floor ?? false;
  // Handle both notional and notional_pricing field names (backend uses notional_pricing), convert string to number
  const notionalValue =
    initialValues.notional_pricing ?? initialValues.notional;
  formValues.notional =
    notionalValue !== undefined && notionalValue !== null
      ? typeof notionalValue === 'string'
        ? Number(notionalValue)
        : notionalValue
      : defaultValuesPricingSummary.notional;
  formValues.dayCount =
    initialValues.day_count ?? defaultValuesPricingSummary.dayCount;
  formValues.frequency = initialValues.frequency
    ? {
        value: String(initialValues.frequency),
        label: String(initialValues.frequency),
      }
    : defaultValuesPricingSummary.frequency;
  formValues.amortRate =
    initialValues.amort_rate !== undefined &&
    initialValues.amort_rate !== null &&
    initialValues.amort_rate !== ''
      ? Number(initialValues.amort_rate)
      : defaultValuesPricingSummary.amortRate;
  formValues.fundingSpread =
    initialValues.funding_spread ?? defaultValuesPricingSummary.fundingSpread;
  formValues.normalVolAdjustment =
    initialValues.normal_vol_adjustment ??
    defaultValuesPricingSummary.normalVolAdjustment;
  formValues.floorStrike =
    initialValues.floor_strike ?? defaultValuesPricingSummary.floorStrike;
  formValues.zeroCostCollar = initialValues.zero_cost_collar ?? false;
  formValues.cftt = initialValues.cftt_enabled ?? false;
  formValues.capMode = initialValues.cap_strike_relative ?? false;
  formValues.enable_hedge_breakage =
    initialValues.enable_hedge_breakage ?? false;
  formValues.debt_and_hedge_enabled =
    initialValues.debt_and_hedge_enabled ?? false;
  formValues.capStrikesList = Array.isArray(initialValues.cap_strikes_list)
    ? initialValues.cap_strikes_list.join(',')
    : initialValues.cap_strikes_list ||
      defaultValuesPricingSummary.capStrikesList;
  formValues.capStrikesAtmBpsList = Array.isArray(
    initialValues.cap_strikes_atm_bps_list,
  )
    ? initialValues.cap_strikes_atm_bps_list.join(',')
    : initialValues.cap_strikes_atm_bps_list ||
      defaultValuesPricingSummary.capStrikesAtmBpsList;
  formValues.hedgePercentage = Array.isArray(initialValues.hedge_percentages)
    ? initialValues.hedge_percentages.join(',')
    : initialValues.hedge_percentages || '';

  // Virtual debt
  formValues.debtType = initialValues.debt_type ?? DEBT_TYPE_FLOAT;
  formValues.debtSpread = initialValues.debt_spread;
  formValues.debtFloor = initialValues.debt_floor;
  formValues.debtCoupon = initialValues.debt_coupon;
  formValues.debtDayCount = initialValues.debt_day_count;
  if (Array.isArray(initialValues.debt_schedule)) {
    formValues.debtSchedule = initialValues.debt_schedule.map((period) => ({
      accrual_start: period.accrual_start,
      accrual_end: period.accrual_end,
      notional: Number(period.notional),
      spread: Number(period.spread),
    }));
    formValues.debtScheduleOverridden = initialValues.debt_schedule.length > 0;
  }

  if (initialValues.graph_data?.length) {
    initialValues.graph_data.forEach((group: any, i: number) => {
      formValues[`graph${i + 1}`] = group.graph ?? false;
      formValues[`swapCharge${i + 1}`] = group.swap_charge ?? 0;
      formValues[`breakage${i + 1}`] = group.breakage ?? [];
      formValues[`effectiveDate${i + 1}`] = group.effective_date ?? '';
      formValues[`maturityDate${i + 1}`] = group.maturity_date ?? '';
    });
  }

  if (initialValues.market_date) {
    formValues.runDate = initialValues.market_date;
  }

  return formValues;
};
